import { enqueueSnackbar } from 'notistack';
import { MetricsClient } from '~/backend/metrics-client';
import { MetricsTypeEnum } from '~/backend/types';
import { useMetricsUpload } from './useMetricsUpload';
import { usePollingQuery, useProcessedResults } from './usePollingResults';

/**
 * A React hook for handling the upload and processing of DICOM files on metrics.
 * This hook integrates the uploading of DICOM files, polling for process completion, and handling the results.
 *
 * processing status, and {@link useProcessedResults} to handle the processed files upon successful completion.
 *
 * @param {MetricsTypeEnum} metricsType - Which metrics model to use.
 * @param {MetricsClient} client - The client used to interact with the backend services for uploading and processing the DICOM files.
 * @param {Object} callbacks - An object containing callback functions.
 * - @param {Function} callbacks.onSuccess - A callback function to execute upon successful processing of the files.
 *
 * @returns {Function} submit - A function that initiates the upload and processing of DICOM files.
 * Takes the model name, an array of DICOM `Blob` objects, and the total count of files as parameters.
 * This function manages the overall process, starting with file upload, then polling for processing status,
 * and finally handling the results upon success, or managing errors if they occur.
 */
export function useUploadAndProcessMetrics(
    metricsType: MetricsTypeEnum,
    client: MetricsClient,
    {
        onSuccess,
    }: {
        onSuccess: (files: File[]) => void;
    }
) {
    const handleProcessedResults = useProcessedResults(client, {
        onSuccess,
        onError: () => {
            enqueueSnackbar('Failed to process metrics files.', {
                variant: 'error',
            });
        },
    });
    const { startPolling, stopPolling } = usePollingQuery(client, {
        onResult: handleProcessedResults,
    });
    const { submit } = useMetricsUpload(client, metricsType, {
        onSuccess: () => {
            startPolling(1000);
        },
        onError: () => {
            stopPolling();
            enqueueSnackbar('Failed to upload selected files.', {
                variant: 'error',
            });
        },
    });

    return submit;
}
